<script setup>
import { Link, useForm } from '@inertiajs/vue3'
import { computed, onUnmounted, ref, watch } from 'vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import ToggleInput from '@/Shared/ToggleInput.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  auth: {
    type: Object,
    default: null
  },
  group: {
    type: Object,
    required: true
  },
  game: {
    type: Object,
    required: true
  }
})

const form = useForm({
  participant_one_set_1: props.game.participant_one_set_1,
  participant_two_set_1: props.game.participant_two_set_1,
  participant_one_set_2: props.game.participant_one_set_2,
  participant_two_set_2: props.game.participant_two_set_2,
  participant_one_set_3: props.game.participant_one_set_3,
  participant_two_set_3: props.game.participant_two_set_3
})

const needsThirdSet = ref(Boolean(form.participant_one_set_3 ?? form.participant_two_set_3))
const scoreInputs = ref({})
const autoAdvanceTimers = ref({})

watch(
  () => needsThirdSet.value,
  (needsSet) => {
    if (!needsSet) {
      form.participant_one_set_3 = null
      form.participant_two_set_3 = null
    }
  }
)

const participantOneName = computed(() => props.game.participant_one.name)
const participantTwoName = computed(() => props.game.participant_two.name)

function toNumber(value) {
  if (value === null || value === '' || typeof value === 'undefined') {
    return null
  }

  const parsed = Number(value)

  return Number.isNaN(parsed) ? null : parsed
}

function setWinner(oneScore, twoScore) {
  const one = toNumber(oneScore)
  const two = toNumber(twoScore)

  if (one === null || two === null || one === two) {
    return null
  }

  return one > two ? 1 : 2
}

const sets = computed(() => {
  return [
    {
      id: 1,
      title: 'Primer set',
      oneField: 'participant_one_set_1',
      twoField: 'participant_two_set_1',
      enabled: true
    },
    {
      id: 2,
      title: 'Segundo set',
      oneField: 'participant_one_set_2',
      twoField: 'participant_two_set_2',
      enabled: true
    },
    {
      id: 3,
      title: 'Desempate',
      oneField: 'participant_one_set_3',
      twoField: 'participant_two_set_3',
      enabled: needsThirdSet.value
    }
  ]
})

const setWins = computed(() => {
  const counters = { one: 0, two: 0 }

  sets.value.forEach((setItem) => {
    if (!setItem.enabled) {
      return
    }

    const winner = setWinner(form[setItem.oneField], form[setItem.twoField])

    if (winner === 1) {
      counters.one += 1
    }

    if (winner === 2) {
      counters.two += 1
    }
  })

  return counters
})

const matchWinnerName = computed(() => {
  if (setWins.value.one === setWins.value.two) {
    return null
  }

  return setWins.value.one > setWins.value.two ? participantOneName.value : participantTwoName.value
})

function registerScoreInput(field) {
  return (element) => {
    if (!element) {
      delete scoreInputs.value[field]
      return
    }

    scoreInputs.value[field] = element
  }
}

function getScoreFieldOrder() {
  const fields = [
    'participant_one_set_1',
    'participant_two_set_1',
    'participant_one_set_2',
    'participant_two_set_2'
  ]

  if (needsThirdSet.value) {
    fields.push('participant_one_set_3', 'participant_two_set_3')
  }

  return fields
}

function focusNextScoreField(field) {
  const fields = getScoreFieldOrder()
  const index = fields.indexOf(field)

  if (index === -1 || index === fields.length - 1) {
    return
  }

  const nextInput = scoreInputs.value[fields[index + 1]]

  if (nextInput) {
    nextInput.focus()
    nextInput.select()
  }
}

function handleScoreInput(field, event) {
  const value = event.target.value?.toString().trim() ?? ''

  if (!value) {
    return
  }

  if (autoAdvanceTimers.value[field]) {
    clearTimeout(autoAdvanceTimers.value[field])
  }

  autoAdvanceTimers.value[field] = window.setTimeout(() => {
    focusNextScoreField(field)
  }, 420)
}

function handleScoreKeydown(field, event) {
  if (event.key === 'Enter') {
    event.preventDefault()
    focusNextScoreField(field)
  }
}

function updateResult() {
  form.put(`/rankings/phases/groups/games/${props.game.id}/result/update`, {
    preserveScroll: true,
    preserveState: false
  })
}

onUnmounted(() => {
  Object.values(autoAdvanceTimers.value).forEach((timer) => {
    clearTimeout(timer)
  })
})
</script>

<template>
  <div class="mx-auto w-full max-w-5xl overflow-x-hidden px-3 pb-8 pt-2 sm:px-6 lg:px-8">
    <div class="mb-4 flex flex-wrap items-center gap-2 break-words text-xs font-medium text-gray-500 sm:text-sm">
      <component
        :is="auth.user ? Link : 'span'"
        :class="auth.user ? 'max-w-full break-all text-indigo-500 hover:text-indigo-700' : 'max-w-full break-all'"
        :href="`/rankings/phases/${group.phase.id}/edit`"
      >
        {{ group.phase.ranking.name }}
      </component>
      <span>/</span>
      <component
        :is="auth.user ? Link : 'span'"
        :class="auth.user ? 'max-w-full break-all text-indigo-500 hover:text-indigo-700' : 'max-w-full break-all'"
        :href="`/rankings/phases/${group.phase.id}/edit`"
      >
        {{ group.phase.name }}
      </component>
      <span>/</span>
      <component
        :is="auth.user ? Link : 'span'"
        :class="auth.user ? 'max-w-full break-all text-indigo-500 hover:text-indigo-700' : 'max-w-full break-all'"
        :href="`/rankings/phases/groups/${group.id}/edit`"
      >
        {{ group.name }}
      </component>
      <span>/</span>
      <span class="text-gray-700">Resultado</span>
    </div>

    <div class="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-white to-indigo-50/30 shadow-sm">
      <div class="border-b border-indigo-100 bg-white/90 p-5 sm:p-6">
        <p class="text-xs font-semibold uppercase tracking-wide text-indigo-500">Partido</p>
        <div class="mt-3 flex items-center justify-between gap-3">
          <div class="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-center">
            <p class="truncate text-sm font-semibold text-gray-900 sm:text-base">
              {{ participantOneName }}
            </p>
          </div>
          <div class="shrink-0 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-600">
            VS
          </div>
          <div class="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-center">
            <p class="truncate text-sm font-semibold text-gray-900 sm:text-base">
              {{ participantTwoName }}
            </p>
          </div>
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-3">
          <div class="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
            <Icon name="Trophy" class="h-4 w-4 text-amber-500" />
            <span>{{ setWins.one }} - {{ setWins.two }} sets</span>
          </div>
          <div
            class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ring-1"
            :class="matchWinnerName ? 'bg-green-50 text-green-700 ring-green-200' : 'bg-gray-50 text-gray-600 ring-gray-200'"
          >
            <span v-if="matchWinnerName">Ganador provisional: {{ matchWinnerName }}</span>
            <span v-else>Sin ganador definido</span>
          </div>
        </div>
      </div>

      <form class="p-5 sm:p-6" @submit.prevent="updateResult">
        <div class="space-y-4">
          <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div
              class="score-grid score-grid-header border-b border-gray-100 bg-gray-50/80"
              :class="{ 'score-grid-three-sets': needsThirdSet }"
            >
              <div class="px-2 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:px-4">
                Jugador
              </div>
              <div class="px-1 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Set 1
              </div>
              <div class="px-1 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Set 2
              </div>
              <div
                v-if="needsThirdSet"
                class="px-1 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Set 3
              </div>
            </div>

            <div class="score-grid border-b border-gray-100" :class="{ 'score-grid-three-sets': needsThirdSet }">
              <div class="truncate px-2 py-4 text-sm font-semibold text-gray-900 sm:px-4 sm:text-base">
                {{ participantOneName }}
              </div>
              <div class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_one_set_1')"
                  v-model="form.participant_one_set_1"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_one_set_1', $event)"
                  @keydown="handleScoreKeydown('participant_one_set_1', $event)"
                />
              </div>
              <div class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_one_set_2')"
                  v-model="form.participant_one_set_2"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_one_set_2', $event)"
                  @keydown="handleScoreKeydown('participant_one_set_2', $event)"
                />
              </div>
              <div v-if="needsThirdSet" class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_one_set_3')"
                  v-model="form.participant_one_set_3"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_one_set_3', $event)"
                  @keydown="handleScoreKeydown('participant_one_set_3', $event)"
                />
              </div>
            </div>

            <div class="score-grid" :class="{ 'score-grid-three-sets': needsThirdSet }">
              <div class="truncate px-2 py-4 text-sm font-semibold text-gray-900 sm:px-4 sm:text-base">
                {{ participantTwoName }}
              </div>
              <div class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_two_set_1')"
                  v-model="form.participant_two_set_1"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_two_set_1', $event)"
                  @keydown="handleScoreKeydown('participant_two_set_1', $event)"
                />
              </div>
              <div class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_two_set_2')"
                  v-model="form.participant_two_set_2"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_two_set_2', $event)"
                  @keydown="handleScoreKeydown('participant_two_set_2', $event)"
                />
              </div>
              <div v-if="needsThirdSet" class="px-1 py-2">
                <input
                  :ref="registerScoreInput('participant_two_set_3')"
                  v-model="form.participant_two_set_3"
                  class="score-input"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  @input="handleScoreInput('participant_two_set_3', $event)"
                  @keydown="handleScoreKeydown('participant_two_set_3', $event)"
                />
              </div>
            </div>
          </div>

          <div v-if="Object.keys(form.errors).length" class="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
            <p
              v-for="(error, key) in form.errors"
              :key="key"
              class="text-xs font-medium text-red-600"
            >
              {{ error }}
            </p>
          </div>

          <div class="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/50 px-4 py-3">
            <ToggleInput
              v-model="needsThirdSet"
              class="flex"
              label="Se jugó desempate (tercer set)"
            />
          </div>
        </div>

        <div class="mt-6 flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
          <LoadingButton :loading="form.processing" class="btn-indigo" type="submit">
            Guardar resultado
          </LoadingButton>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.score-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(2.8rem, 3.35rem) minmax(2.8rem, 3.35rem);
  align-items: center;
}

.score-grid > * {
  min-width: 0;
}

.score-grid-three-sets {
  grid-template-columns:
    minmax(0, 1fr)
    minmax(2.8rem, 3.35rem)
    minmax(2.8rem, 3.35rem)
    minmax(2.8rem, 3.35rem);
}

.score-grid-header {
  min-height: 2.75rem;
}

.score-input {
  width: 100%;
  height: 2.75rem;
  border-radius: 0.65rem;
  border: 1px solid #d1d5db;
  background: #ffffff;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  outline: none;
  padding: 0;
}

.score-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

@media (min-width: 640px) {
  .score-grid {
    grid-template-columns: minmax(0, 1fr) 5rem 5rem;
  }

  .score-grid-three-sets {
    grid-template-columns: minmax(0, 1fr) 5rem 5rem 5rem;
  }

  .score-input {
    height: 3.25rem;
    font-size: 1.375rem;
  }
}

@media (max-width: 420px) {
  .score-grid {
    grid-template-columns: minmax(0, 1fr) 2.6rem 2.6rem;
  }

  .score-grid-three-sets {
    grid-template-columns: minmax(0, 1fr) 2.6rem 2.6rem 2.6rem;
  }

  .score-input {
    height: 2.45rem;
    font-size: 1rem;
  }
}
</style>
